import React, { useEffect, useState, useRef } from 'react';

/**
 * Modal que se muestra a usuarios no-admin cuando el admin activa el modo
 * mantenimiento con grace period. Muestra cuenta regresiva, dispara un
 * evento 'app:force-save' para que los formularios guarden inmediatamente,
 * y al llegar a 0 cierra la sesión del usuario.
 *
 * Props:
 *   isOpen: boolean
 *   initialSeconds: int (segundos restantes al abrirse)
 *   onLogout: () => void  (callback para cerrar sesión + redirect)
 */
const MaintenanceCountdownModal = ({ isOpen, initialSeconds, onLogout }) => {
  const [seconds, setSeconds] = useState(initialSeconds || 0);
  const [savedCount, setSavedCount] = useState(0);
  const fired = useRef(false);

  // Disparar force-save una sola vez al abrirse
  useEffect(() => {
    if (!isOpen || fired.current) return;
    fired.current = true;
    setSeconds(initialSeconds || 0);
    // Disparar evento global - los formularios escuchan y guardan
    try {
      window.dispatchEvent(new CustomEvent('app:force-save', {
        detail: {
          reason: 'maintenance',
          onSaved: () => setSavedCount(c => c + 1),
        }
      }));
    } catch (e) { /* noop */ }
  }, [isOpen, initialSeconds]);

  // Countdown
  useEffect(() => {
    if (!isOpen) return;
    if (seconds <= 0) {
      onLogout && onLogout();
      return;
    }
    const t = setTimeout(() => setSeconds(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [isOpen, seconds, onLogout]);

  // Resetear cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      fired.current = false;
      setSavedCount(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
        <div className="text-5xl mb-3">🛠️</div>
        <h2 className="text-2xl font-bold text-amber-700 mb-2">
          Sistema entrando en mantenimiento
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          El administrador activó el modo mantenimiento. Estamos <strong>guardando tu trabajo automáticamente</strong>. Tu sesión se cerrará en:
        </p>
        <div className="text-6xl font-black text-amber-600 mb-4 tabular-nums">
          {seconds}s
        </div>
        {savedCount > 0 && (
          <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">
            ✓ Trabajo guardado correctamente ({savedCount} {savedCount === 1 ? 'formulario' : 'formularios'})
          </p>
        )}
        <p className="text-xs text-gray-500">
          Cuando se cierre tu sesión podrás volver a iniciar sesión una vez el administrador desactive el mantenimiento.
        </p>
      </div>
    </div>
  );
};

export default MaintenanceCountdownModal;
